import { Link } from 'react-router-dom';
import {
  ICP_DEVENV_ID,
  ICP_DEVENV_NAME,
  ICP_DEVENV_LAYER,
  ICP_DEVENV_PRIORITY,
  ICP_NEXT_PACKAGE,
  DEPENDS_ON,
  IMPLEMENTS,
  BOOTSTRAP_TEAM,
  PREREQUISITES,
  LOCAL_SERVICES,
  ENVIRONMENT_CONFIG,
  IDE_TOOLING,
  MAKEFILE_TARGETS,
  BOOTSTRAP_WORKFLOW,
  CI_PARITY_CHECKS,
  READINESS_GATES,
  SECRETS_POLICY,
  OBSERVABILITY_BOOTSTRAP,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_TEAM,
  DEMO_PREREQUISITES,
  DEMO_LOCAL_SERVICES,
  DEMO_ENV_CONFIG,
  DEMO_IDE_TOOLING,
  DEMO_BOOTSTRAP_WORKFLOW,
  DEMO_CI_PARITY,
  DEMO_EXECUTIVE,
  buildBootstrapDashboard,
} from '../../devenvbootstrap/framework';
import {
  BootstrapTeamPanel,
  PrerequisitesPanel,
  LocalServicesPanel,
  EnvironmentConfigPanel,
  IDEToolingPanel,
  BootstrapWorkflowPanel,
  CIParityPanel,
  ExecutiveBootstrapDashboardPanel,
} from '../../components/devenvbootstrap';
import { Card, KpiCard } from '../../components/ui';

export function DevEnvironmentBootstrapPage() {
  const dashboard = buildBootstrapDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{ICP_DEVENV_ID} · {ICP_DEVENV_PRIORITY}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{ICP_DEVENV_NAME}</h1>
        <p className="mt-1 text-sm text-slate-500">
          P0 foundation package — bootstrap every developer workstation and local environment to production parity.
          Architecture layer: {ICP_DEVENV_LAYER}. Next: {ICP_NEXT_PACKAGE}.
        </p>
        <p className="mt-1 text-xs text-slate-400">Depends on: {DEPENDS_ON.join(' · ')} · Implements: {IMPLEMENTS.join(', ')}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <KpiCard label="Prerequisites" value={dashboard.prerequisites.toString()} />
        <KpiCard label="Local Services" value={dashboard.local_services.toString()} />
        <KpiCard label="Env Variables" value={dashboard.environment_vars.toString()} />
        <KpiCard label="Bootstrap Steps" value={dashboard.bootstrap_steps.toString()} />
        <KpiCard label="CI Parity" value={dashboard.ci_parity_checks.toString()} />
        <KpiCard label="Readiness Gates" value={dashboard.readiness_gates.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Bootstrap Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Bootstrap Team</h2>
        <p className="mt-1 text-sm text-slate-500">{BOOTSTRAP_TEAM.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BootstrapTeamPanel members={DEMO_TEAM} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Prerequisites</h2>
        <p className="mt-1 text-sm text-slate-500">{PREREQUISITES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PrerequisitesPanel items={DEMO_PREREQUISITES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Local Services (Docker Compose)</h2>
        <p className="mt-1 text-sm text-slate-500">{LOCAL_SERVICES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <LocalServicesPanel items={DEMO_LOCAL_SERVICES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Environment Configuration</h2>
        <p className="mt-1 text-sm text-slate-500">{ENVIRONMENT_CONFIG.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">Secrets policy: {SECRETS_POLICY.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <EnvironmentConfigPanel items={DEMO_ENV_CONFIG} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">IDE Tooling & Makefile</h2>
        <p className="mt-1 text-sm text-slate-500">{IDE_TOOLING.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{MAKEFILE_TARGETS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <IDEToolingPanel items={DEMO_IDE_TOOLING} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Bootstrap Workflow</h2>
        <p className="mt-1 text-sm text-slate-500">{BOOTSTRAP_WORKFLOW.join(' → ')}</p>
        <Card className="mt-4" padding="sm">
          <BootstrapWorkflowPanel items={DEMO_BOOTSTRAP_WORKFLOW} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">CI Parity & Readiness</h2>
        <p className="mt-1 text-sm text-slate-500">{CI_PARITY_CHECKS.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">Gates: {READINESS_GATES.join(' · ')} · Observability: {OBSERVABILITY_BOOTSTRAP.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CIParityPanel items={DEMO_CI_PARITY} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Bootstrap Dashboard</h2>
        <Card className="mt-4" padding="sm">
          <ExecutiveBootstrapDashboardPanel items={DEMO_EXECUTIVE} />
        </Card>
      </section>
    </div>
  );
}
